import React from 'react'
import Link from '../helper/link'

import TimeRange from '../app/timeRange'
import { MonitorOperation } from '../domain/Operation'
import LargeNumber from '../helper/large-number'
import Memory from '../helper/memory'
import Milliseconds from '../helper/milliseconds'
import Percent from '../helper/percent'
import Sparkline, { SparklineDataPoint } from '../helper/sparkline'
import Tendency from '../helper/tendency'
import { parseTransactionName, TransactionName } from '../ui/transactionName'
import DropdownComponent from './operation/dropdown'
import TracepointIndicator from './operation/tracepoint-indicator'

export type ComparisonReference = {
    type: string
    referenceId: string
}

interface Props {
    operation: MonitorOperation
    sparkline: SparklineDataPoint[]
    range: TimeRange
    route: string
    parameters: { [key: string]: string }
    loading: boolean
    full: boolean
    compact: boolean
    tagged?: { [tag: string]: MonitorOperation }
    showTaggedTransactions: boolean
    menuType: TransactionMenuType
    isTideways6: boolean
    showBenchmark: boolean
    comparisonReference?: ComparisonReference | null
}

export enum TransactionMenuType {
    None = 0,
    Edit = 1,
    Compare = 2,
}

class TransactionTableRowItem extends React.Component<Props> {
    public static displayName = 'Operation'
    public static defaultProps = {
        range: null,
        sparkline: null,
        route: 'XhprofProfilerBundle.Transaction.show',
        parameters: {},
        loading: false,
        full: false,
        compact: false,
        showTaggedTransactions: true,
        menuType: TransactionMenuType.None,
        showBenchmark: false,
    }

    public render() {
        let icon = null
        const {
            showTaggedTransactions,
            operation,
            range,
            route,
            compact,
            sparkline,
            full,
            tagged,
            loading,
            menuType,
            comparisonReference,
        } = this.props

        if (operation.change) {
            const iconMap = {
                new: 'fa fa-plus-circle',
                up: 'fa fa-chevron-circle-up',
                down: 'fa fa-chevron-circle-down',
                same: 'fa fa-circle-o',
            }
            icon = iconMap[operation.change]
        }

        let parameters = {
            ...this.props.parameters,
            operation: operation.id,
        }

        if (range) {
            parameters = {
                ...parameters,
                ...range.toParameters(),
            }
        }

        return (
            <>
                <tr className="reveal-on-hover-parent">
                    <td>
                        {operation.new ? (
                            <span
                                className="label label-outline-default tw-mr-8"
                                title="This Transaction was not present in the 'previous' timeframe."
                            >
                                new
                            </span>
                        ) : null}
                        {route ? (
                            <Link route={route} params={parameters}>
                                <TransactionName operation={operation} icon={icon} full={full} />
                            </Link>
                        ) : (
                            <TransactionName operation={operation} icon={icon} full={full} />
                        )}

                        {operation.name === 'default' ? (
                            <span className={'tw-font-size-12'}>
                                {' '}
                                (
                                <a
                                    target="_blank"
                                    href={
                                        'https://support.tideways.com/documentation/reference/faq/default-transaction.html?utm_medium=app&utm_source=transaction&utm_campaign=default-transaction'
                                    }
                                    rel="noreferrer"
                                >
                                    What is this transaction?
                                </a>
                                )
                            </span>
                        ) : null}

                        {this.props.showBenchmark ? this.renderBenchmark(operation) : null}

                        {!operation.prioritized ? null : (
                            <small>
                                <span className="label label-burning" style={{ marginLeft: '1em' }}>
                                    <i className="fa fa-crosshairs" /> prioritized
                                </span>
                            </small>
                        )}
                        {showTaggedTransactions && tagged ? (
                            <div className="tw-ml-16 text-muted text-ellipsis tw-line-height-30">
                                grouped by tags
                                {Object.keys(tagged)
                                    .map(tag => tagged[tag])
                                    .map(taggedTransaction => (
                                        <Link
                                            key={taggedTransaction.id}
                                            route={route}
                                            params={{ ...parameters, operation: taggedTransaction.id }}
                                        >
                                            <span className="label label-outline-default tw-ml-8">
                                                {parseTransactionName(taggedTransaction.identifier).tag}
                                                <TracepointIndicator operation={taggedTransaction} />
                                            </span>
                                        </Link>
                                    ))}
                            </div>
                        ) : null}
                    </td>

                    {!operation.requests ? null : (
                        <React.Fragment>
                            <td className="number">
                                <LargeNumber value={operation.requests} />{' '}
                                {operation.requestsChange !== null ? (
                                    <Tendency value={operation.requestsChange / 100} showValue={false} lessIsGood={false} />
                                ) : null}
                            </td>
                            {compact ? null : (
                                <td className="number">
                                    <Milliseconds value={operation.averageResponse * 1000} />{' '}
                                    {operation.averageResponseChange !== null ? (
                                        <Tendency value={operation.averageResponseChange / 100} showValue={false} />
                                    ) : null}
                                </td>
                            )}
                            <td className="number">
                                {!operation.active && this.props.isTideways6 ? (
                                    <span
                                        className="tw-tooltip tw-tooltip-dataText tw-tooltip--bottom"
                                        data-tooltip-text="Transactions with limited visibility don't collect detailed monitoring data"
                                    >
                                        <i className="fa fa-ban text-muted" />
                                    </span>
                                ) : (
                                    <>
                                        {operation.responseTimeIncident ? (
                                            <Link
                                                route="XhprofErrorBundle.Issues.incident"
                                                parameters={{ error: operation.responseTimeIncident }}
                                                style={{ color: '#f56954' }}
                                                title="View incident for transaction in this period"
                                            >
                                                <span className="fa fa-warning"></span>{' '}
                                            </Link>
                                        ) : null}
                                        <Milliseconds value={operation.maxResponse * 1000} />{' '}
                                        {operation.maxResponseChange !== null ? (
                                            <Tendency value={operation.maxResponseChange / 100} showValue={false} />
                                        ) : null}
                                    </>
                                )}
                            </td>
                            {compact ? null : (
                                <React.Fragment>
                                    <td className="number">
                                        {!operation.active && this.props.isTideways6 ? (
                                            <span
                                                className="tw-tooltip tw-tooltip-dataText tw-tooltip--bottom"
                                                data-tooltip-text="Transactions with limited visibility don't collect detailed monitoring data"
                                            >
                                                <i className="fa fa-ban text-muted" />
                                            </span>
                                        ) : (
                                            <>
                                                <Memory value={operation.avgMemory * 1024} />
                                                {operation.avgMemoryChange !== null ? (
                                                    <Tendency value={operation.avgMemoryChange / 100} showValue={false} />
                                                ) : null}
                                            </>
                                        )}
                                    </td>
                                    <td className="number">
                                        {operation.failureRateIncident ? (
                                            <Link
                                                route="XhprofErrorBundle.Issues.incident"
                                                parameters={{ error: operation.failureRateIncident }}
                                                style={{ color: '#f56954' }}
                                                title="View incident for transaction in this period"
                                            >
                                                <span className="fa fa-warning"></span>{' '}
                                            </Link>
                                        ) : null}
                                        <LargeNumber value={operation.errors} class="error" defaultValue="0" />{' '}
                                        {operation.errorsChange ? (
                                            <Tendency value={operation.errorsChange / 100} showValue={false} />
                                        ) : null}
                                    </td>
                                </React.Fragment>
                            )}
                            <td className="number">
                                <Percent value={operation.impact / 100} />
                            </td>
                            {menuType === TransactionMenuType.Edit ? (
                                <td>
                                    <div className="pull-right reveal-on-hover">
                                        <div className="dropdown">
                                            <a href="#" className="dropdown-toggle" data-toggle="dropdown">
                                                <i className="fa fa-ellipsis-h tw-color-grey-600" />
                                            </a>
                                            <DropdownComponent alignment="right">
                                                <Link route="XhprofProfilerBundle.Transaction.edit" params={{ operation: operation.id }}>
                                                    Edit Transaction
                                                </Link>
                                                {operation.active ? (
                                                    <Link
                                                        route="XhprofProfilerBundle.Transaction.deactivate"
                                                        params={{ operation: operation.id }}
                                                        query={{ origin: 'parent' }}
                                                    >
                                                        Change to &quot;Limited&quot; Visibility
                                                    </Link>
                                                ) : null}
                                                {!operation.active ? (
                                                    <Link
                                                        route="XhprofProfilerBundle.Transaction.activate"
                                                        params={{ operation: operation.id }}
                                                        query={{ origin: 'parent' }}
                                                    >
                                                        Change to &quot;Full&quot; Visibility
                                                    </Link>
                                                ) : null}
                                                {!operation.ignored ? (
                                                    <Link
                                                        route="XhprofProfilerBundle.Transaction.ignore"
                                                        params={{ operation: operation.id }}
                                                        query={{ origin: 'details' }}
                                                    >
                                                        Ignore Transaction
                                                    </Link>
                                                ) : null}
                                            </DropdownComponent>
                                        </div>
                                    </div>
                                </td>
                            ) : null}
                            {menuType === TransactionMenuType.Compare && comparisonReference ? (
                                <td>
                                    <div className="pull-right reveal-on-hover">
                                        <Link
                                            className="tw-tooltip tw-tooltip-dataText tw-tooltip--left"
                                            data-tooltip-text="Compare traces before and after the event using trace aggregation."
                                            route="XhprofProfilerBundle.Event.traceAggregationSaga"
                                            params={{
                                                ...parameters,
                                                event: comparisonReference.referenceId,
                                                type: comparisonReference.type,
                                            }}
                                        >
                                            <i className="fa fa-exchange"></i>
                                        </Link>
                                    </div>
                                </td>
                            ) : null}
                        </React.Fragment>
                    )}

                    {!operation.requests && !loading ? <td colSpan={6}>No requests in period.</td> : null}

                    {loading ? (
                        <td colSpan={4} className="text-muted">
                            Loading…
                        </td>
                    ) : null}

                    {!sparkline || compact ? null : (
                        <td>
                            <Sparkline data={sparkline} />
                        </td>
                    )}
                </tr>
            </>
        )
    }

    private renderBenchmark(operation: MonitorOperation) {
        const benchmarks = {
            'Shopware\\Storefront\\Controller\\NavigationController::index': {
                responseGold: 287,
                responseSilver: 463,
                framework: 'Shopware 6',
            },
            'Shopware\\Storefront\\Controller\\ProductController::index': {
                responseGold: 303,
                responseSilver: 399,
                framework: 'Shopware 6',
            },
            'Shopware\\Storefront\\Controller\\NavigationController::home': {
                responseGold: 51,
                responseSilver: 127,
                framework: 'Shopware 6',
            },
            'Shopware\\Storefront\\Controller\\SearchController::search': {
                responseGold: 303,
                responseSilver: 607,
                framework: 'Shopware 6',
            },
        }

        if (!benchmarks[operation.name]) {
            return null
        }

        const benchmark = benchmarks[operation.name]

        let color = ''
        let section = ''
        if (operation.averageResponse < benchmark.responseGold) {
            color = '#FFD700'
            section = '10%'
        } else if (operation.averageResponse < benchmark.responseSilver) {
            color = '#838996'
            section = '25%'
        } else {
            return null
        }

        return (
            <a
                href="https://tideways.com/profiler/blog/shopware-6-performance-benchmark-report-q1-2025?utm_medium=app&utm_source=history&utm_campaign=shopware-benchmark"
                target="_blank"
                className="tw-pl-4 tw-tooltip tw-tooltip-dataText tw-tooltip--right"
                data-tooltip-text={
                    'In the best ' + section + ' average response times of ' + benchmark.framework + ' shops in Q1 2025 benchmark report'
                }
                rel="noreferrer"
            >
                <span className="fa fa-trophy" style={{ color: color }} />
            </a>
        )
    }
}

export default TransactionTableRowItem
